import type { LearningStyleTag, CourseProfile, CourseProfileLevel } from '../types/courseProfile';
import { SUBJECT_CATALOG, getSubjectById } from './subjectCatalog';
import { EXAM_LEVEL_OPTIONS } from './examAiParams';

/** Etiqueta visible del curso = nivel curricular (sin nombre libre). */
export function resolveCourseDisplayName(
  profile: Pick<CourseProfile, 'level' | 'displayName'>,
): string {
  const levelLabel = EXAM_LEVEL_OPTIONS.find((option) => option.id === profile.level)?.label;
  return levelLabel || profile.displayName.trim() || 'Curso';
}

/** IDs de asignatura del curso (principal + adicionales). */
export function resolveCourseSubjectIds(
  profile: Pick<CourseProfile, 'subjectId' | 'additionalSubjectIds'>,
): string[] {
  const primary = String(profile.subjectId || '').trim();
  const extras = Array.isArray(profile.additionalSubjectIds)
    ? profile.additionalSubjectIds.map((id) => String(id || '').trim()).filter(Boolean)
    : [];
  return [...new Set([primary, ...extras].filter(Boolean))];
}

/** Tags de asignatura del curso (principal + adicionales). */
export function resolveCourseSubjectTags(
  profile: Pick<CourseProfile, 'subjectId' | 'subjectTag' | 'additionalSubjectIds'>,
): string[] {
  return resolveCourseSubjectIds(profile)
    .map((id) => getSubjectById(id)?.tag || id)
    .map((tag) => String(tag || '').trim().toLowerCase())
    .filter(Boolean);
}

/** Aplica lista completa de asignaturas: la primera es principal. */
export function applyCourseSubjectIds(
  profile: CourseProfile,
  subjectIds: string[],
): CourseProfile {
  const unique = [
    ...new Set(subjectIds.map((id) => String(id || '').trim()).filter(Boolean)),
  ].filter((id) => SUBJECT_CATALOG.some((subject) => subject.id === id && subject.id !== 'general'));
  if (!unique.length) return profile;
  const primary = getSubjectById(unique[0]);
  if (!primary) return profile;
  return {
    ...profile,
    subjectId: primary.id,
    subjectTag: primary.tag,
    additionalSubjectIds: unique.slice(1),
  };
}

/**
 * Borrador de curso sin inventar level/subject fantasma.
 * El editor exige elegir nivel antes de guardar; subjectId debe ser explícito.
 */
export function createEmptyCourseProfile(
  subjectId: string,
  options?: { schoolName?: string; level?: CourseProfileLevel },
): CourseProfile {
  const subject = SUBJECT_CATALOG.find((item) => item.id === subjectId);
  if (!subject) {
    throw new Error(`Asignatura desconocida: ${subjectId}`);
  }
  const level = options?.level;
  return {
    id: '',
    displayName: level
      ? resolveCourseDisplayName({ level, displayName: '' })
      : 'Curso',
    schoolName: options?.schoolName ?? '',
    subjectId: subject.id,
    subjectTag: subject.tag,
    additionalSubjectIds: [],
    // Sin default 5basico: el docente elige nivel en el editor (StudioPage ya valida).
    level: (level ?? '') as CourseProfileLevel,
    interests: [],
    learningStyle: [],
    strengths: [],
    difficulties: [],
  };
}

export const LEARNING_STYLE_OPTIONS: Array<{ id: LearningStyleTag; label: string; icon: string }> = [
  { id: 'visual', label: 'Visual', icon: '👁️' },
  { id: 'kinestesico', label: 'Kinestésico', icon: '🤸' },
  { id: 'auditivo', label: 'Auditivo', icon: '👂' },
  { id: 'lectoescritor', label: 'Lectoescritor', icon: '📝' },
  { id: 'colaborativo', label: 'Colaborativo', icon: '🧑‍🤝‍🧑' },
  { id: 'individual', label: 'Individual', icon: '🎯' },
  { id: 'gamificado', label: 'Gamificado', icon: '🎮' },
  { id: 'proyecto', label: 'Proyecto', icon: '🛠️' },
];

export const VOCAL_RANGE_OPTIONS = [
  { id: 'children' as const, label: 'Infantil' },
  { id: 'adolescent' as const, label: 'Adolescente' },
  { id: 'mixed' as const, label: 'Mixto' },
];
